import { AuthCredential, linkWithCredential, unlink } from "firebase/auth";

import { Dispatch, GetState } from "common/types";

import { getFirebaseAuth, getFirebaseUserName } from "services/firebase/auth";
import { getUserLinkedProviders } from "services/firebase/auth/ssoProvider";
import { ProviderSSO } from "services/firebase/auth/types";

import { fetchMySchoolInfo } from "seneca-common/features/schools/service";
import {
  getRegion,
  signInSuccess,
  signUpSuccess
} from "seneca-common/features/user/state";
import {
  analyticsLogSignIn,
  analyticsLogSignUp
} from "seneca-common/utils/senecaAnalytics";

import { signInWithEmailAndPassword } from "features/user/actions/auth/signInWithEmailAndPassword";
import { userAccountType } from "features/user/consts";

import { setUserAsSignedUpBefore } from "../../../utils/signedUpBefore";
import { setRegionPreference } from "../../preferences";
import { fetchUserInfo, saveUserInfo } from "../../userInfo";
import { fetchAdditionalUserInfoOnSignIn } from "../signInWithEmailAndPassword";
import { reconcileData } from "../utils";

type ArgsSignUp = {
  response: any;
  region: string;
  providerName: ProviderSSO;
  onSignUpSuccess?: () => void;
};

export function proceedUserSignUp({
  response,
  region,
  providerName,
  onSignUpSuccess
}: ArgsSignUp) {
  return async function (dispatch: Dispatch, getState: GetState) {
    const {
      user: { email, displayName }
    } = response;
    const userDetails = {
      email,
      ...getFirebaseUserName(displayName)
    };

    await dispatch(
      saveUserInfo({
        ...userDetails,
        type: userAccountType.schoolstudent,
        providerData: getUserLinkedProviders()
      })
    );

    region && dispatch(setRegionPreference(region));

    await dispatch(reconcileData());

    setUserAsSignedUpBefore();

    dispatch(signUpSuccess());

    analyticsLogSignUp(getState(), providerName);

    onSignUpSuccess && onSignUpSuccess();
  };
}

type ArgsSignIn = {
  region: string;
  providerName: ProviderSSO;
  onSignInSuccess?: () => void;
};

export function proceedUserSignIn({
  onSignInSuccess,
  region,
  providerName
}: ArgsSignIn) {
  return async function (dispatch: Dispatch, getState: GetState) {
    try {
      await Promise.all([
        dispatch(fetchUserInfo()),
        dispatch(fetchMySchoolInfo())
      ]);
    } catch (err: any) {}

    await dispatch(reconcileData());

    setUserAsSignedUpBefore();

    const persistedRegion = getRegion(getState());

    if (!persistedRegion && region) {
      dispatch(setRegionPreference(region));
    }

    dispatch(signInSuccess());
    await fetchAdditionalUserInfoOnSignIn(dispatch);

    onSignInSuccess && onSignInSuccess();

    analyticsLogSignIn(getState(), providerName);
  };
}

export function isLoadedFromTeams(): boolean {
  return navigator.userAgent.includes("Teams");
}

type AttemptFirstMISLoginArgs = {
  region: string;
  providerName: ProviderSSO;
  onSignInSuccess?: () => void;
  credential: AuthCredential;
  email: string;
  onPasswordConfirmation: (info: {
    credential: AuthCredential;
    email: string;
  }) => void;
};

const MIS_PASSWORD = "seneca2020";

export function attemptFirstMISLogin({
  region,
  providerName,
  onSignInSuccess,
  credential,
  email,
  onPasswordConfirmation
}: AttemptFirstMISLoginArgs) {
  return async function (dispatch: Dispatch) {
    try {
      await dispatch(signInWithEmailAndPassword(email, MIS_PASSWORD));

      await linkWithCredential(getFirebaseAuth().currentUser!, credential);
      await unlink(getFirebaseAuth().currentUser!, "password");

      await dispatch(
        proceedUserSignIn({
          onSignInSuccess,
          region,
          providerName
        })
      );
    } catch (err: any) {
      onPasswordConfirmation({
        credential,
        email
      });
    }
  };
}
